import React, { useRef, useState } from 'react';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import 'quill-table-ui/dist/index.css';
import { FiMaximize } from 'react-icons/fi';
import QuillTableUI from 'quill-table-ui';
import Table from 'quill/modules/table';
import { useTranslation } from 'react-i18next';

// Register table UI and module
Quill.register({ 'modules/tableUI': QuillTableUI });
Quill.register('modules/table', Table);

// interface CustomToolbarProps {
//   onFullscreenToggle: () => void;
//   isFullscreen: boolean;
// }

interface MyEditorProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
}

const formats = ['bold', 'italic', 'underline', 'font', 'size', 'link'];

const modules = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ font: ['Source Sans Pro', 'Arial', 'Times New Roman'] }],
    [{ size: ['small', 'normal', 'large'] }],
    ['link'],
  ],
};

// const CustomToolbar: React.FC<CustomToolbarProps> = ({
//   onFullscreenToggle,
//   isFullscreen,
// }) => (
//   <div
//     id="toolbar"
//     className="custom-toolbar"
//     style={{
//       display: 'flex',
//       gap: '8px',
//       padding: '8px',
//       borderBottom: '1px solid #eee',
//       backgroundColor: '#fff',
//     }}
//   >
//     {/* Basic Formatting */}
//     <button className="ql-bold" />
//     <button className="ql-italic" />
//     <button className="ql-underline" />

//     {/* Font Family */}
//     <select className="ql-font" defaultValue="Source Sans Pro">
//       <option value="Source Sans Pro">Source Sans Pro</option>
//       <option value="Arial">Arial</option>
//       <option value="Times New Roman">Times New Roman</option>
//     </select>

//     {/* Font Size */}
//     <select className="ql-size">
//       <option value="normal" selected>
//         Normal
//       </option>
//       <option value="small">Small</option>
//       <option value="large">Large</option>
//     </select>

//     {/* Link */}
//     <button className="ql-link" />

//     {/* Fullscreen Toggle */}
//     <button
//       onClick={onFullscreenToggle}
//       title="Fullscreen"
//       className="custom-icon-button"
//     >
//       <FiMaximize size={16} />
//     </button>

//     {/* Help */}
//     <button className="ql-help" title="Help">
//       ?
//     </button>
//   </div>
// );

const MyEditor: React.FC<MyEditorProps> = ({ value, onChange, onBlur }) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggleFullscreen = () => {
    if (wrapperRef.current) {
      wrapperRef.current.classList.toggle('fullscreen');
      setIsFullscreen(!isFullscreen);
    }
  };
  const {t} = useTranslation();

  return (
    <div
      id="editor-wrapper"
      ref={wrapperRef}
      style={{
        width: '100%',
        border: '1px solid #eee',
        borderRadius: '4px',
        overflow: 'hidden',
      }}
      className={isFullscreen ? 'fullscreen' : ''}
    >
      <div
        className="editor-header"
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          padding: '8px',
          borderBottom: '1px solid #eee',
        }}
      >
        <button
          onClick={toggleFullscreen}
          title="Fullscreen"
          className="custom-icon-button"
        >
          <FiMaximize size={16} />
        </button>
      </div>
      <ReactQuill
        theme="snow"
        modules={modules}
        formats={formats}
        placeholder={t("common.startTyping")}
        style={{
          height: '300px',
          backgroundColor: '#fff',
          padding: 0,
        }}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
      />
      <style>
        {`
          .fullscreen {
            position: fixed !important;
            top: 0;
            left: 0;
            width: 100vw !important;
            height: 100vh !important;
            z-index: 9999;
            background: white;
          }
          
          .fullscreen .ql-editor {
            height: calc(100vh - 100px) !important;
          }
 
          .ql-snow .ql-toolbar button,
          .ql-snow .ql-picker {
            width: 28px;
            height: 28px;
            padding: 3px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border: 1px solid #eee;
            border-radius: 4px;
            margin: 0 2px;
          }
 
          .ql-snow .ql-formats {
            display: inline-flex;
            align-items: center;
            gap: 5px;
            margin-right: 10px;
          }
 
          .custom-icon-button {
            width: 28px;
            height: 28px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border: 1px solid #eee;
            border-radius: 4px;
            background: white;
            cursor: pointer;
          }
 
          .custom-icon-button:hover {
            background: #f8f9fa;
          }
 
          .ql-snow .ql-picker.ql-font {
            width: 150px;
          }
 
          .ql-snow .ql-picker.ql-size {
            width: 100px;
          }
 
          .ql-snow .ql-picker-label {
            border: 1px solid #eee;
            border-radius: 4px;
            display: flex;
            align-items: center;
            height: 28px;
            padding: 0 8px;
          }
 
          .ql-snow .ql-picker-options {
            border: 1px solid #eee;
            border-radius: 4px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          }
 
          .ql-toolbar.ql-snow {
            border: none;
            padding: 8px;
            display: flex;
            align-items: center;
          }
 
          .ql-container.ql-snow {
            border: none;
            border-top: 1px solid #eee;
          }
 
          .ql-editor {
            min-height: 200px;
            font-family: 'Source Sans Pro', sans-serif;
            font-size: 14px;
            line-height: 1.5;
          }
 
          .ql-editor p {
            margin-bottom: 1em;
          }
        `}
      </style>
    </div>
  );
};

export default MyEditor;
